import { lazy, Suspense, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";
import { StudioShell } from "./components/StudioShell";
import { RemoteConnectGate } from "./components/RemoteConnectGate";
import { IntroLanding } from "./components/IntroLanding";
import { AndroidLaunchIntro } from "./components/AndroidLaunchIntro";
import { useStudio } from "./studioStore";
import { hasRemoteConfig } from "./lib/remoteConfig";
import { TOKEN_KEY } from "./lib/backendClient";

const viewComponents = {
  home: lazy(() => import("./views/HomeDashboard").then((module) => ({ default: module.HomeDashboard }))),
  designer: lazy(() => import("./views/DesignerStudio").then((module) => ({ default: module.DesignerStudio }))),
  code: lazy(() => import("./views/CodeWorkspace").then((module) => ({ default: module.CodeWorkspace }))),
  data: lazy(() => import("./views/DataStudio").then((module) => ({ default: module.DataStudio }))),
  workflows: lazy(() => import("./views/WorkflowStudio").then((module) => ({ default: module.WorkflowStudio }))),
  dashboard: lazy(() => import("./views/DashboardStudio").then((module) => ({ default: module.DashboardStudio }))),
  git: lazy(() => import("./views/GitCenter").then((module) => ({ default: module.GitCenter }))),
  deploy: lazy(() => import("./views/DeployCenter").then((module) => ({ default: module.DeployCenter }))),
  assistant: lazy(() => import("./views/AssistantStudio").then((module) => ({ default: module.AssistantStudio }))),
  backend: lazy(() => import("./views/BackendStudio").then((module) => ({ default: module.BackendStudio }))),
  integrations: lazy(() => import("./views/IntegrationsStudio").then((module) => ({ default: module.IntegrationsStudio }))),
  "help-guide": lazy(() => import("./views/HelpCenter").then((module) => ({ default: module.HelpGuideView }))),
  "help-feedback": lazy(() => import("./views/HelpCenter").then((module) => ({ default: module.FeedbackView }))),
  "help-knowledge": lazy(() => import("./views/HelpCenter").then((module) => ({ default: module.KnowledgeBaseView }))),
  settings: lazy(() => import("./views/SettingsStudio").then((module) => ({ default: module.SettingsStudio }))),
};

const ANDROID_INTRO_SESSION_KEY = "blackred.androidLaunchIntro.v1";

function shouldShowAndroidIntro() {
  if (Capacitor.getPlatform() !== "android") return false;
  try {
    return sessionStorage.getItem(ANDROID_INTRO_SESSION_KEY) !== "shown";
  } catch {
    return true;
  }
}

export function App() {
  const { t } = useTranslation();
  const { view, cloudStatus } = useStudio();
  const ActiveView = viewComponents[view];
  // Todas las plataformas exigen una sesión cloud. Electron ya no salta esta puerta por el
  // mero hecho de tener APIs nativas: el almacenamiento local es una capacidad de suscripción.
  const [remoteReady, setRemoteReady] = useState(() => hasRemoteConfig() && Boolean(sessionStorage.getItem(TOKEN_KEY)));
  // Una cuenta conocida sin sesión vuelve directamente al formulario de acceso.
  const [introSeen, setIntroSeen] = useState(() => hasRemoteConfig());
  const [showAndroidIntro, setShowAndroidIntro] = useState(shouldShowAndroidIntro);

  function finishAndroidIntro() {
    try {
      sessionStorage.setItem(ANDROID_INTRO_SESSION_KEY, "shown");
    } catch {
      // La intro sigue funcionando aunque el WebView bloquee el almacenamiento de sesión.
    }
    setShowAndroidIntro(false);
  }

  let content: React.ReactNode;

  if (!remoteReady) {
    if (!introSeen) {
      content = <IntroLanding onContinue={() => setIntroSeen(true)} />;
    } else {
      content = <RemoteConnectGate onConnected={() => { setRemoteReady(true); window.location.reload(); }} />;
    }
  } else if (cloudStatus === "loading") {
    content = <section className="view backend-loading card cloud-boot"><span className="eyebrow">BlackRed Cloud</span><h2>{t("app.loadingProject")}</h2></section>;
  } else if (cloudStatus === "error") {
    content = <section className="view backend-loading card cloud-boot"><span className="eyebrow">BlackRed Cloud</span><h2>{t("app.projectLoadErrorTitle")}</h2><p>{t("app.projectLoadErrorBody")}</p><button className="btn primary" onClick={() => window.location.reload()}>{t("app.retry")}</button></section>;
  } else {
    content = (
      <StudioShell>
        <Suspense fallback={<div className="backend-loading card"><span>{t("app.loadingModule")}</span></div>}>
          <ActiveView />
        </Suspense>
      </StudioShell>
    );
  }

  return (
    <>
      {content}
      {showAndroidIntro && <AndroidLaunchIntro onComplete={finishAndroidIntro} />}
    </>
  );
}
